import React, { useEffect, useState } from "react";
import { Card, Group, Text, Button, Stack } from "@mantine/core";
import moment from "moment";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import { useCellRender } from "../hooks/useCellRender";
import EntityTabAnchor from "../pages/account/payroll2/EntityTabAnchor";

const TransactionMatchedCommissionStatement = ({ form, disabled }) => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [cellRender] = useCellRender();
  const [statement, setStatement] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const id = form?.values?._id;
  const matchType = form?.values?.matchType;
  const commissionStatementRef = form?.values?.commissionStatement;
  const commissionStatementId =
    commissionStatementRef?._id || commissionStatementRef || null;

  const fetchData = async () => {
    try {
      setLoadError(null);
      if (!commissionStatementId) {
        setStatement(null);
        return;
      }
      const result = await api.getById({
        apiEntity: "commissionStatement",
        id: commissionStatementId,
      });
      if (!result) {
        setStatement(null);
        setLoadError(
          t("bankTransaction.matchedCommissionStatement.loadError")
        );
        return;
      }
      setStatement(result);
    } catch (error) {
      console.log("error", error);
      setStatement(null);
      setLoadError(
        error?.message ||
          t("bankTransaction.matchedCommissionStatement.loadError")
      );
    }
  };

  useEffect(() => {
    fetchData();
  }, [commissionStatementId]);

  const unlink = async () => {
    if (
      !window.confirm(t("bankTransaction.matchedCommissionStatement.unlinkConfirm"))
    ) {
      return;
    }
    try {
      await api.BankTransaction.unlinkCommissionStatement(id);
      toast.success(t("bankTransaction.matchedCommissionStatement.unlinkSuccess"));
      form.reload();
      fetchData();
    } catch (error) {
      toast.error(error?.message || t("bankTransaction.matchedCommissionStatement.unlinkError"));
    }
  };

  if (matchType !== "COMMISSION_STATEMENT" || !commissionStatementId) {
    return (
      <Text color="dimmed" align="center" mt="md">
        {t("bankTransaction.matchedCommissionStatement.notLinked")}
      </Text>
    );
  }

  if (loadError) {
    return (
      <Text color="red" align="center" mt="md">
        {loadError}
      </Text>
    );
  }

  if (!statement) {
    return (
      <Text color="dimmed" align="center" mt="md">
        {t("bankTransaction.matchedCommissionStatement.loading")}
      </Text>
    );
  }

  return (
    <>
      <Card withBorder>
        <Stack spacing="xs">
          <Group position="apart">
            <Text size="xs" color="dimmed">
              {t("bankTransaction.matchedCommissionStatement.statement")}
            </Text>
            <EntityTabAnchor
              entity="commissionStatement"
              id={statement._id}
              label={statement.code}
              size="sm"
            />
          </Group>
          <Group position="apart">
            <Text size="xs" color="dimmed">
              {t("bankTransaction.matchedCommissionStatement.period")}
            </Text>
            <Text size="xs">{statement.period || "-"}</Text>
          </Group>
          <Group position="apart">
            <Text size="xs" color="dimmed">
              {t("bankTransaction.linkCommissionStatement.netDeposit")}
            </Text>
            <Text size="xs">
              {cellRender.Currency(statement.receivedNetAmount ?? form.values.deposit)}
            </Text>
          </Group>
          <Group position="apart">
            <Text size="xs" color="dimmed">
              {t("bankTransaction.bankCharge")}
            </Text>
            <Text size="xs">
              {cellRender.Currency(statement.bankCharge ?? form.values.bankCharge ?? 0)}
            </Text>
          </Group>
          <Group position="apart">
            <Text size="xs" color="dimmed">
              {t("bankTransaction.linkCommissionStatement.grossAmount")}
            </Text>
            <Text size="xs" weight={600}>
              {cellRender.Currency(
                statement.receivedGrossAmount ??
                  (form.values.deposit || 0) + (form.values.bankCharge || 0)
              )}
            </Text>
          </Group>
          <Group position="apart">
            <Text size="xs" color="dimmed">
              {t("bankTransaction.matchedCommissionStatement.status")}
            </Text>
            <Text size="xs">{statement.status}</Text>
          </Group>
          {form.values.matchedDate && (
            <Group position="apart">
              <Text size="xs" color="dimmed">
                {t("bankTransaction.matchedCommissionStatement.linkedDate")}
              </Text>
              <Text size="xs">{moment(form.values.matchedDate).format("LL")}</Text>
            </Group>
          )}
        </Stack>
      </Card>
      {!disabled && (
        <Button onClick={unlink} size="xs" mt="xl" color="red">
          {t("bankTransaction.matchedCommissionStatement.unlink")}
        </Button>
      )}
    </>
  );
};

export default TransactionMatchedCommissionStatement;
